import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../queries";
import Instant from "./Instant";
import type { Notification, NotificationKind } from "../types";

const KIND_LABEL: Record<NotificationKind, string> = {
  verdict_review: "review",
  spec_gap: "spec gap",
  run_assigned: "assigned",
  human_sitting: "sitting",
  physical_action: "action",
  blocked_target: "blocked",
};

/** A sitting is answered scenario by scenario in the run's sitting drawer, not with one click. */
function answersInSitting(notification: Notification): boolean {
  return notification.kind === "human_sitting" && Boolean(notification.run_id);
}

function openTarget(notification: Notification): { to: string; external: boolean } | null {
  if (answersInSitting(notification)) {
    return { to: `/runs/${notification.run_id}?sitting=1`, external: false };
  }
  if (notification.action_url) {
    return { to: notification.action_url, external: /^https?:\/\//.test(notification.action_url) };
  }
  if (notification.run_id) return { to: `/runs/${notification.run_id}`, external: false };
  return null;
}

/**
 * "Your inbox" — the real human task queue (Phase 5).
 *
 * Distinct from the derived "Needs a human" signals: these are tasks that were
 * raised for a person or a pool. While the person is present they live here; when
 * they are not, the sweep delegates them to Mission Control and qa-tron, so the
 * row still shows and acting here cancels the follow-up.
 */
export default function InboxPanel({ actor }: { actor?: string | null }) {
  const queryClient = useQueryClient();
  const [mineOnly, setMineOnly] = useState(true);

  const params = useMemo(() => {
    const p = new URLSearchParams({ limit: "12" });
    if (mineOnly && actor) p.set("actor", actor);
    return p;
  }, [mineOnly, actor]);

  const query = useQuery({
    queryKey: ["inbox", params.toString()],
    queryFn: () => api.listInbox(params),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["inbox"] });
  };
  const seen = useMutation({ mutationFn: (id: string) => api.seenNotification(id), onSuccess: invalidate });
  const act = useMutation({ mutationFn: (id: string) => api.actNotification(id), onSuccess: invalidate });
  const dismiss = useMutation({
    mutationFn: (id: string) => api.dismissNotification(id),
    onSuccess: invalidate,
  });

  const items = query.data?.items ?? [];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={`btn btn-xs ${mineOnly ? "btn-active" : ""}`}
          onClick={() => setMineOnly((v) => !v)}
          disabled={!actor}
          title={actor ? undefined : "Sign in to filter to your tasks"}
        >
          Mine
        </button>
        <span className="text-xs opacity-60">
          {query.data ? `${query.data.total} open` : "…"}
        </span>
      </div>

      {query.isLoading && <span className="loading loading-spinner loading-sm" />}
      {!query.isLoading && items.length === 0 && (
        <p className="text-sm opacity-60">
          Your inbox is clear — nothing has been raised for you or your pool.
        </p>
      )}

      <ul className="divide-y divide-base-300 rounded-lg bg-base-200">
        {items.map((n) => {
          const target = openTarget(n);
          const busy = seen.isPending || act.isPending || dismiss.isPending;
          return (
            <li key={n.id} className="density-row px-3 py-2.5">
              <div className="flex items-center gap-2">
                <span className="badge badge-outline badge-sm">{KIND_LABEL[n.kind]}</span>
                <span className="font-medium truncate">{n.title}</span>
                <span className="ml-auto text-xs opacity-50 whitespace-nowrap">
                  <Instant iso={n.created_at} />
                </span>
              </div>
              {n.body && <p className="text-xs opacity-70 mt-1 line-clamp-2">{n.body}</p>}
              <div className="flex items-center gap-1 mt-2">
                {target &&
                  (target.external ? (
                    <a className="btn btn-xs btn-ghost" href={target.to} target="_blank" rel="noreferrer">
                      Open ↗
                    </a>
                  ) : answersInSitting(n) ? (
                    <Link
                      className="btn btn-xs btn-primary"
                      to={target.to}
                      title="Record pass, fail or blocked for each scenario"
                    >
                      Answer
                    </Link>
                  ) : (
                    <Link className="btn btn-xs btn-ghost" to={target.to}>
                      Open
                    </Link>
                  ))}
                {n.state === "pending" && (
                  <button
                    type="button"
                    className="btn btn-xs btn-ghost"
                    disabled={busy}
                    onClick={() => seen.mutate(n.id)}
                  >
                    Seen
                  </button>
                )}
                {!answersInSitting(n) && (
                  <button
                    type="button"
                    className="btn btn-xs btn-primary"
                    disabled={busy}
                    onClick={() => act.mutate(n.id)}
                  >
                    Done
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-xs btn-ghost"
                  disabled={busy}
                  onClick={() => dismiss.mutate(n.id)}
                >
                  Dismiss
                </button>
                {n.mc_task_id && (
                  <span
                    className="ml-auto text-xs opacity-50"
                    title="Also delivered through Mission Control and qa-tron"
                  >
                    delegated
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {(seen.isError || act.isError || dismiss.isError) && (
        <p className="text-error text-xs">Action failed.</p>
      )}
    </div>
  );
}
